/**
 * FNA Report Page - Load and display a saved FNA report by ID
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAgentAuth } from '../../../contexts/AgentAuthContext';
import { getFnaReport } from '../../../lib/agents/fnaStorage';
import { useAgentToolUsage } from '../../../hooks/useAgentToolUsage';
import { TOOL_IDS } from '../../../lib/agents/firestore-schema';
import FNAReportView from './FNAReportView';
import { Button } from '../../../components/ui/button';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';

export default function FNAReportPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { agentProfile } = useAgentAuth();
  const usage = useAgentToolUsage(agentProfile, TOOL_IDS.FINANCIAL_NEED_ANALYSIS);
  const { toast } = useToast();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      if (!reportId) {
        setError('Report ID required');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const doc = await getFnaReport(reportId);
        if (!doc) {
          setError('Report not found');
          setReport(null);
        } else {
          setReport(doc.reportData || null);
        }
      } catch (err) {
        console.error('Failed to load report:', err);
        setError(err.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [reportId]);

  const handleBack = () => {
    navigate('/agents/financial-need-analysis');
  };

  const handleDownload = async (reportElement) => {
    if (!reportElement || !report) return;

    try {
      toast({ title: 'Generating PDF', description: 'Building your report — this takes about 10 seconds…' });

      await new Promise((r) => setTimeout(r, 400));

      const html2pdf = (await import('html2pdf.js')).default;

      const fullWidth = reportElement.scrollWidth || reportElement.offsetWidth || 794;
      const fullHeight = reportElement.scrollHeight || reportElement.offsetHeight;

      const opt = {
        margin: [8, 8],
        filename: `GrowAndStayRich-FNA-${(report.client1?.name || 'client').replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          letterRendering: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: fullWidth,
          windowHeight: fullHeight,
          scrollX: 0,
          scrollY: 0,
          onclone: (clonedDoc) => {
            clonedDoc.querySelectorAll('*').forEach((el) => {
              const s = el.style;
              if (!s) return;
              if (s.overflow && s.overflow !== 'visible') s.overflow = 'visible';
              if (s.overflowY && s.overflowY !== 'visible') s.overflowY = 'visible';
              if (s.overflowX && s.overflowX !== 'visible') s.overflowX = 'visible';
              if (s.maxHeight) s.maxHeight = 'none';
              if (s.height && (s.height.includes('calc') || s.height.includes('vh'))) s.height = 'auto';
              // SVG elements have SVGAnimatedString for className — use baseVal to get a plain string
              const c = typeof el.className === 'string' ? el.className : (el.className?.baseVal ?? '');
              if (c.includes('overflow') || c.includes('max-h-')) {
                s.overflow = 'visible';
                s.maxHeight = 'none';
                s.height = 'auto';
              }
            });
          },
        },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
        pagebreak: { mode: ['css', 'legacy'] },
      };

      let pdfBlob = null;
      if (agentProfile?.uid && agentProfile?.email) {
        try {
          pdfBlob = await html2pdf().set(opt).from(reportElement).outputPdf('blob');
        } catch (e) {}
      }
      await html2pdf().set(opt).from(reportElement).save();

      if (pdfBlob && agentProfile?.uid) {
        try {
          const { savePdfReport } = await import('../../../lib/agents/premiumReportGenerator');
          await savePdfReport({
            userId: agentProfile.uid,
            userEmail: agentProfile.email,
            toolId: TOOL_IDS.FINANCIAL_NEED_ANALYSIS,
            reportData: report,
            pdfBlob,
          });
        } catch (e) {}
      }
      usage.recordPdfExport({ clientName: report.client1?.name, reportType: 'premium_html' });
      toast({ title: 'PDF downloaded', description: 'Report saved successfully.' });
    } catch (err) {
      console.error('PDF generation error:', err);
      toast({ title: 'Download failed', description: err.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-10 h-10 animate-spin mb-4" />
        <p>Loading report…</p>
      </div>
    );
  }

  if (error || !report) {
    return (
      <div className="space-y-4">
        <Button variant="outline" onClick={() => navigate('/agents/financial-need-analysis')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to FNA Clients
        </Button>
        <div className="text-center py-12 text-muted-foreground">
          <p className="font-medium">{error || 'Report not found'}</p>
          <p className="text-sm mt-1">The report may have been deleted or you may not have access.</p>
        </div>
      </div>
    );
  }

  return (
    <FNAReportView
      report={report}
      onBack={handleBack}
      onDownload={handleDownload}
      backLabel="Back to FNA Clients"
    />
  );
}
